import { Component, Event, EventEmitter, h, Prop } from '@stencil/core';
import { NavigationThemeType } from '../../components';
import { RoutingKeys, RoutingKeyWithClick } from '@phn/entities/routing-key';
import { NavigationMode, NavigationModeType, NavigationTheme } from '@phn/utility/constants';
import { breakpoint } from '@porsche-design-system/components-js/styles';
import pcomIcons from './pcom-level-0-icons';
import shopIcons from './shop-level-0-icons';
import { navContentStore } from '@phn/state/nav-content-store';
import featureToggles from '@phn/services/feature-toggles';
import {
  burgerButtonSkeleton,
  burgerButtonSkeletonDark,
  pureButtonSkeleton,
  pureButtonSkeletonDark
} from '../../utility/skeleton';
import { isPcomSearchAvailable } from '../../utility/pcom-search-helper';
import { isShopAvailable } from '@phn/utility/helper/shop-helper';
import { goTo } from '@phn/services/routing-manager';
import { navStateStore } from '@phn/state/nav-state-store';
import finderIcons from './finder-level-0-icons';
import { ApplicationType, Application, ContextualIconResponse } from '@phn/utility/constants/app';
import { isDesktop, IsKeyboardClick } from '@phn/utility/helper';

@Component({
  tag: 'phn-level-0-icons',
  styleUrl: 'level-0-icons.scss',
  scoped: true
})
export class Level0Icons {
  @Prop() locale!: string;
  @Prop() theme: NavigationThemeType = NavigationTheme.light;
  @Prop() mode: NavigationModeType = NavigationMode.navbar;
  @Prop() app: ApplicationType = Application.default;
  @Prop() breakpoint!: number;
  @Prop() isLoading!: boolean;
  @Prop() isNavigationLoaded!: boolean;

  @Event() openDrawer!: EventEmitter<RoutingKeyWithClick>;

  render(): HTMLPhnLevel0IconsElement {
    const contextualIconsProps = {
      locale: this.locale,
      app: this.app,
      mode: this.mode,
      theme: this.theme,
      isNavigationLoaded: this.isNavigationLoaded,
      isDesktop: isDesktop(this.breakpoint)
    };

    const IconsStrategies: Record<string, () => ContextualIconResponse> = {
      pcom: () => pcomIcons(contextualIconsProps),
      shop: () => shopIcons(contextualIconsProps),
      icc: () => ({ leftIcons: null, rightIcons: null }),
      finder: () =>
        featureToggles.isFinderShortcutsToggleActive()
          ? finderIcons(contextualIconsProps)
          : { leftIcons: null, rightIcons: null },
      default: () => ({ leftIcons: null, rightIcons: null })
    };

    const iconsStrategy = IconsStrategies[this.app];
    const contextualIcons = iconsStrategy();
    const hasMarketPorscheID = navContentStore.state.myPorsche;
    return (
      <div class="contextual-icons">
        <div class="left-icons">
          <phn-burger-button
            theme={this.theme}
            mode={this.mode}
            hideLabel={
              isPcomSearchAvailable(this.locale, this.app) || isShopAvailable(this.locale, this.app)
                ? this.breakpoint <= breakpoint.xs
                : this.breakpoint < breakpoint.xs
            }
            alignLabel={'end'}
            isLoading={this.isLoading}
            onClick={(e: MouseEvent) => {
              this.openDrawer.emit({ initialRoutingKey: '', isKeyboardClick: IsKeyboardClick(e) });
            }}
            style={this.isNavigationLoaded ? {} : { display: 'none' }}
          />
          {!this.isNavigationLoaded
            ? this.mode === NavigationMode.hero && this.theme === NavigationTheme.dark
              ? burgerButtonSkeletonDark()
              : burgerButtonSkeleton()
            : null}

          {this.breakpoint <= breakpoint.xs ? contextualIcons.leftIcons : null}
        </div>

        <slot></slot>

        <div class="right-icons">
          {this.breakpoint > breakpoint.xs ? contextualIcons.leftIcons : null}
          {contextualIcons.rightIcons}

          {hasMarketPorscheID ? (
            <div class="icon">
              <phn-my-porsche-icon-button
                mode={this.mode}
                theme={this.theme}
                onClick={(e: MouseEvent) => {
                  if (isDesktop(this.breakpoint) || featureToggles.isNaviDrawerFromCDNEnabled()) {
                    this.openDrawer.emit({
                      initialRoutingKey: RoutingKeys.MY_PORSCHE,
                      isKeyboardClick: IsKeyboardClick(e)
                    });
                  } else {
                    goTo(RoutingKeys.MY_PORSCHE);
                    navStateStore.state.usingKeyboard = IsKeyboardClick(e);
                  }
                }}
                style={this.isNavigationLoaded ? {} : { display: 'none' }}
              />

              {!this.isNavigationLoaded
                ? this.mode === NavigationMode.hero && this.theme === NavigationTheme.dark
                  ? pureButtonSkeletonDark()
                  : pureButtonSkeleton()
                : null}
            </div>
          ) : null}
        </div>
      </div>
    ) as HTMLPhnLevel0IconsElement;
  }
}
